import { Skeleton } from "antd5";

import { TextLink } from "components/actions/Links";
import { getDocumentTypeLabel } from "components/documents/utils";
import OrganisationsContainer from "components/record_details/OrganisationsPopover";
import Alert from "components/ui/alert/Alert";
import Popover from "components/ui/popover/Popover";
import Tooltip from "components/ui/tooltip/Tooltip";
import { PropertyInline } from "lib/core_components/Property";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { LINK_REGEX } from "lib/utils";
import { useDocumentTracking } from "./tracking";
import { tooltips } from "./utils";

import css from "./DocumentDetails.module.scss";

type DocumentDetailsProps = {
  buyers: { id: string; name: string }[];
  type: string;
  publishDate: string;
  sourceUrls: { id: string; sourceUrl?: string | null }[];
  alternativeSource: string;
  isLoading: boolean;
  isInFallbackMode?: boolean;
};

export default function DocumentDetails({
  buyers,
  type,
  publishDate,
  sourceUrls,
  alternativeSource,
  isLoading,
  isInFallbackMode = false,
}: DocumentDetailsProps) {
  const { data: currentUser } = useCurrentUser();
  const sourceUrlLinks = sourceUrls
    .filter((source) => !!source.sourceUrl)
    .map((source) => String(source.sourceUrl));

  return (
    <div className={css.infoContainer}>
      <h3>Details</h3>
      {isInFallbackMode && (
        <Alert type="warning">
          We couldn’t fully process this document, so some of the preview and features aren’t
          available right now.
        </Alert>
      )}
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <PropertyInline
            title="Buyer(s)"
            value={<OrganisationsContainer orgs={buyers} orgType="buyers" />}
          />
          <PropertyInline title="Type" value={getDocumentTypeLabel(type, currentUser)} />
          <PropertyInline
            title="Published"
            value={publishDate ? publishDate.split("T")[0] : <EmptyState />}
          />
          <PropertyInline
            title="Source(s)"
            value={
              <SourcesComponent sourceUrls={sourceUrlLinks} alternativeSource={alternativeSource} />
            }
            valueClassName={
              !!sourceUrls.length || alternativeSource !== "" ? css.ellipsis : undefined
            }
          />
        </>
      )}
    </div>
  );
}

function SourcesComponent({
  sourceUrls,
  alternativeSource,
}: {
  sourceUrls: string[];
  alternativeSource: string;
}) {
  if (sourceUrls.length) {
    return <SourceUrls sources={sourceUrls} />;
  }
  if (alternativeSource) {
    return LINK_REGEX.test(alternativeSource) ? (
      <SourceUrls sources={[alternativeSource]} />
    ) : (
      <AlternativeSource source={alternativeSource} />
    );
  }
  return <EmptyState />;
}

function SourceUrls({ sources }: { sources: string[] }) {
  const tracking = useDocumentTracking();
  const sourcesTotal = sources.length;

  if (sources.length === 1) {
    return (
      <TextLink
        to={sources[0]}
        targetType="new-tab"
        onClick={tracking.documentSourceViewed}
        noreferrer
      >
        {sources[0]}
      </TextLink>
    );
  }

  return (
    <Popover trigger={<span>{`${sourcesTotal} sources`}</span>} side="bottom">
      <div className={css.popoverTitle}>{`${sourcesTotal} sources`}</div>
      <div className={css.popoverContent}>
        {sources.map((source) => (
          <TextLink
            to={source}
            targetType="new-tab"
            noreferrer
            className={css.ellipsis}
            onClick={tracking.documentSourceViewed}
            key={source}
          >
            {source}
          </TextLink>
        ))}
      </div>
    </Popover>
  );
}

function EmptyState() {
  return (
    <Tooltip content={tooltips.emptyState}>
      <span className={css.emptyState}>No data found</span>
    </Tooltip>
  );
}

function AlternativeSource({ source }: { source: string }) {
  return (
    <Tooltip content={tooltips.alternativeSource}>
      <span className={css.emptyState}>{source}</span>
    </Tooltip>
  );
}
